// Data structure › Profiles (spec/dashboard.md section 5.5): the read-only tree of the branch's cloud-save schema —
// every profile_root template, its parameters, and under each profile_data parameter the nested profile template,
// recursively (depth-limited); every node links to its template editor; a side list names the profile templates no
// root reaches.
import { useMemo } from 'react';
import { Link } from 'react-router';
import type { Template } from '../../api/types';
import { typeLabel } from '../../content/schema';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { FolderTree, Layers } from '../../ui/icons';
import { SchemaTabs, useSchemaEnv } from './common';
import './schema.css';

const MAX_DEPTH = 6;

/** The profile templates reachable from the roots (through profile_data parameters, lists of them included). */
export function reachableProfiles(templates: Template[]): Set<string> {
  const byName = new Map(templates.map((t) => [t.name, t]));
  const seen = new Set<string>();
  const walk = (t: Template, depth: number) => {
    if (depth > MAX_DEPTH) return;
    for (const p of t.effectiveParameters) {
      const target = p.type === 'profile_data' ? p.template : p.type === 'list' && p.of?.type === 'profile_data' ? p.of.template : null;
      if (target && !seen.has(target)) {
        seen.add(target);
        const nested = byName.get(target);
        if (nested) walk(nested, depth + 1);
      }
    }
  };
  for (const root of templates.filter((t) => t.kind === 'profile_root')) walk(root, 0);
  return seen;
}

export function ProfilesPage() {
  const { catalog } = useSchemaEnv();
  const roots = useMemo(() => catalog.templateList.filter((t) => t.kind === 'profile_root').sort((a, b) => a.name.localeCompare(b.name)), [catalog.templateList]);
  const reached = useMemo(() => reachableProfiles(catalog.templateList), [catalog.templateList]);
  const unused = useMemo(() => catalog.templateList.filter((t) => t.kind === 'profile' && !reached.has(t.name)).sort((a, b) => a.name.localeCompare(b.name)), [catalog.templateList, reached]);

  return (
    <section className="schema-page">
      <div className="page-head">
        <h1>Data structure</h1>
        {!catalog.loading && (
          <span className="page-meta">
            {roots.length} root{roots.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <SchemaTabs />
      <div className="profiles-layout">
        <div className="pane" aria-label="Profile tree">
          <div className="pane-head">
            <h2>Cloud-save schema</h2>
            <span className="muted small">Every profile root and the profile templates it nests</span>
          </div>
          <div className="pane-body">
            {catalog.loading && roots.length === 0 ? (
              <div className="skeleton-rows" role="status" aria-label="Loading">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="skeleton-row">
                    <span className="skeleton" />
                    <span className="skeleton" />
                  </div>
                ))}
              </div>
            ) : roots.length === 0 ? (
              <EmptyState icon={<FolderTree />} title="No profile root" body="A template of kind Profile root holds a player's cloud save; its profile_data parameters nest Profile templates." />
            ) : (
              <ul className="tree profile-tree" role="tree" aria-label="Profiles">
                {roots.map((r) => (
                  <TemplateNode key={r.name} template={r} depth={0} trail={[]} />
                ))}
              </ul>
            )}
          </div>
        </div>
        <aside className="pane" aria-label="Unused profile templates">
          <div className="pane-head">
            <h2>Unused profile templates</h2>
            {unused.length > 0 && <span className="chip">{unused.length}</span>}
          </div>
          {unused.length === 0 ? (
            <p className="pane-body muted small">Every profile template is reached from a root.</p>
          ) : (
            <div className="list-pane">
              {unused.map((t) => (
                <div key={t.name} className="list-row static">
                  <Layers className="icon muted" aria-hidden="true" />
                  <span className="text">
                    <span className="title">
                      <TemplateLink name={t.name} label={t.displayName ?? t.name} />
                    </span>
                    <code className="sub">{t.name}</code>
                  </span>
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}

function TemplateLink({ name, label }: { name: string; label: string }) {
  const { org, gameSlug, branch } = useSchemaEnv();
  return <Link to={routes.template(org, gameSlug, branch, name)}>{label}</Link>;
}

function TemplateNode({ template, depth, trail }: { template: Template; depth: number; trail: string[] }) {
  const { catalog, org, gameSlug, branch } = useSchemaEnv();
  return (
    <li role="treeitem" aria-expanded aria-label={template.name}>
      <span className="tree-node">
        <TemplateLink name={template.name} label={template.displayName ?? template.name} /> <code className="small">{template.name}</code>
      </span>
      <ul role="group">
        {template.effectiveParameters.map((p) => {
          const target = p.type === 'profile_data' ? p.template : p.type === 'list' && p.of?.type === 'profile_data' ? p.of.template : null;
          const nested = target ? catalog.templates.get(target) : undefined;
          const editor = `${routes.template(org, gameSlug, branch, p.declaredBy ?? template.name)}?param=${encodeURIComponent(p.name)}`;
          const cycle = !!target && (trail.includes(target) || target === template.name);
          return (
            <li key={p.name} role="treeitem" aria-label={p.name} aria-expanded={nested && !cycle && depth < MAX_DEPTH ? true : undefined}>
              <span className="tree-node">
                <Link to={editor}>{p.name}</Link> <span className="muted small">{typeLabel(p, catalog.enums, catalog.templates)}</span>
                {cycle && <span className="chip">recursive</span>}
                {target && !nested && <span className="chip chip-danger">unknown template {target}</span>}
              </span>
              {nested && !cycle && depth < MAX_DEPTH && (
                <ul role="group">
                  <TemplateNode template={nested} depth={depth + 1} trail={[...trail, template.name]} />
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </li>
  );
}
